/**
 * Sprint 51: InventoryDashboard — farm portfolio view.
 *
 * Shows the farm's economic value across three categories:
 *   1. Field Inventory   — standing crops (yield × area × price)
 *   2. Stored Inventory  — grain in storage × market price
 *   3. Subsidy Receivables — approved/submitted applications
 *   4. Known Fixed Costs — land lease, loans, insurance (annualized)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SourceBadge from './SourceBadge';
import { getCropColor } from '../config/cropColors';

function formatEur(value) {
  if (value == null || value === 0) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(value);
}


function StatusDot({ status }) {
  const colors = { good: '#22c55e', watch: '#f59e0b', problem: '#ef4444' };
  return (
    <span
      className="inline-block w-2 h-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: colors[status] || '#9E9E9E' }}
    />
  );
}

// ── Hero / Summary section ─────────────────────────────────────────────────

function HeroSection({ summary }) {
  const { t } = useTranslation();
  if (!summary) return null;

  const total = summary.total_inventory_value;
  const fieldPct = total > 0 ? (summary.total_field_value / total) * 100 : 0;
  const storedPct = total > 0 ? (summary.total_stored_value / total) * 100 : 0;
  const subsidyPct = total > 0 ? (summary.total_subsidy_receivable / total) * 100 : 0;

  const segments = [
    { label: t('inventory.fieldStock'), value: summary.total_field_value, pct: fieldPct, color: 'var(--aif-color-primary)' },
    { label: t('inventory.storage'), value: summary.total_stored_value, pct: storedPct, color: '#f59e0b' },
    { label: t('inventory.subsidies'), value: summary.total_subsidy_receivable, pct: subsidyPct, color: '#22c55e' },
  ];

  return (
    <div data-testid="hero-section" className="bg-[var(--aif-bg-surface)] rounded-2xl p-5 shadow-sm border border-[var(--aif-border-default)] mb-4">
      <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('inventory.title')}</div>
      <div className="text-3xl font-bold text-[var(--aif-text-primary)] mb-3">
        {formatEur(total)}
      </div>

      {/* Stacked progress bar */}
      <div className="flex h-2 rounded-full overflow-hidden mb-4 bg-[var(--aif-bg-elevated)]">
        {segments.map(s => s.pct > 0 && (
          <div key={s.label} style={{ width: `${s.pct}%`, backgroundColor: s.color }} />
        ))}
      </div>

      {/* Sub-total row */}
      <div className="grid grid-cols-3 gap-2">
        {segments.map(s => (
          <div key={s.label} className="text-center">
            <div className="text-[10px] text-[var(--aif-text-muted)] leading-tight mb-0.5">
              {s.label}
            </div>
            <div className="text-sm font-semibold text-[var(--aif-text-primary)]">
              {formatEur(s.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Net asset value */}
      {summary.known_fixed_costs_annual > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--aif-border-default)] flex justify-between items-center">
          <span className="text-xs text-[var(--aif-text-muted)]">{t('inventory.netAssetValue')}</span>
          <span className={`text-sm font-bold ${summary.net_asset_value >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {formatEur(summary.net_asset_value)}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Field Inventory section ─────────────────────────────────────────────────

function FieldInventorySection({ fields, onFieldTap }) {
  const { t } = useTranslation();
  if (!fields || fields.length === 0) {
    return (
      <div className="text-center py-6">
        <div className="text-2xl mb-2">🌾</div>
        <p className="text-sm text-[var(--aif-text-muted)]">{t('inventory.noFields')}</p>
      </div>
    );
  }

  return (
    <div data-testid="field-inventory-section">
      <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2 flex items-center gap-2">
        🌾 {t('inventory.fieldStock')}
        <span className="text-xs font-normal text-[var(--aif-text-muted)]">{fields.length} {t('inventory.fields')}</span>
      </h3>
      <div className="space-y-2">
        {fields.map(field => (
          <FieldCard key={field.field_id} field={field} onTap={onFieldTap} />
        ))}
      </div>
    </div>
  );
}

function FieldCard({ field, onTap }) {
  const { t } = useTranslation();
  const cropColor = getCropColor(field.crop).bg;

  return (
    <button
      data-testid={`field-card-${field.field_id}`}
      onClick={() => onTap?.(field.field_id)}
      className="w-full text-left bg-[var(--aif-bg-surface)] rounded-xl shadow-sm border border-[var(--aif-border-default)] overflow-hidden hover:border-[var(--aif-color-primary)] transition-colors"
    >
      <div className="flex">
        {/* Crop color bar */}
        <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: cropColor }} />
        <div className="flex-1 px-3 py-2.5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-[var(--aif-text-primary)] leading-tight">
                {field.field_name}
              </div>
              <div className="text-xs text-[var(--aif-text-muted)] mt-0.5">
                {field.crop_name_localized || field.crop} · {field.area_ha?.toLocaleString('de-DE')} ha
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-base font-bold text-[var(--aif-text-primary)]">
                {formatEur(field.field_value_eur)}
              </div>
              {field.days_to_harvest != null && (
                <div className="text-[10px] text-[var(--aif-text-muted)]">
                  {t('inventory.harvestIn', { days: field.days_to_harvest })}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <SourceBadge source={field.yield_source} />
            <SourceBadge source={field.price_source} />
            {field.risk_factors?.includes('gloez_violation') && (
              <span className="text-[9px] bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded px-1 py-0.5">
                ⚠️ GLÖZ 7
              </span>
            )}
            {field.risk_factors?.includes('price_below_90d_avg') && (
              <span className="text-[9px] bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 rounded px-1 py-0.5">
                📉 {t('inventory.priceBelow')}
              </span>
            )}
          </div>
          <div className="text-[10px] text-[var(--aif-text-muted)] mt-1">
            {field.expected_yield_t_ha} t/ha × {field.area_ha} ha × {field.market_price_eur_t} EUR/t
          </div>
        </div>
      </div>
    </button>
  );
}

// ── Stored Inventory section ────────────────────────────────────────────────

function StoredInventorySection({ items }) {
  const { t } = useTranslation();
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-[var(--aif-text-muted)]">{t('inventory.noStoredItems')}</p>
      </div>
    );
  }

  return (
    <div data-testid="stored-inventory-section">
      <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">
        🏗️ {t('inventory.storage')}
      </h3>
      <p className="text-[10px] text-[var(--aif-text-muted)] mb-2">{t('inventory.storagePriceNote')}</p>
      <div className="space-y-2">
        {items.map(item => (
          <div
            key={item.item_id}
            data-testid={`stored-item-${item.item_id}`}
            className="bg-[var(--aif-bg-surface)] rounded-xl shadow-sm border border-[var(--aif-border-default)] px-3 py-2.5"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium text-[var(--aif-text-primary)]">{item.item_name}</div>
                <div className="text-xs text-[var(--aif-text-muted)] mt-0.5">
                  {item.quantity_t?.toLocaleString('de-DE')} {item.unit}
                  {item.storage_location && ` · ${item.storage_location}`}
                </div>
              </div>
              <div className="text-right">
                <div className="text-base font-bold text-[var(--aif-text-primary)]">
                  {formatEur(item.stored_value_eur)}
                </div>
                <div className="flex items-center gap-1 justify-end mt-0.5">
                  <span className="text-[10px] text-[var(--aif-text-muted)]">
                    {item.market_price_eur_t?.toLocaleString('de-DE')} EUR/t
                  </span>
                  <SourceBadge source={item.price_source} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Subsidy Receivables section ─────────────────────────────────────────────

function SubsidySection({ receivables }) {
  const { t } = useTranslation();
  if (!receivables || receivables.length === 0) return null;

  const total = receivables.reduce((s, r) => s + (r.amount_eur || 0), 0);

  return (
    <div data-testid="subsidy-section">
      <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-1">
        💰 {t('inventory.subsidies')}
      </h3>
      <div className="text-xs text-[var(--aif-text-muted)] mb-2">
        {t('inventory.subsidyTotal')}: {formatEur(total)}
      </div>
      <div className="space-y-2">
        {receivables.map(r => (
          <div
            key={r.program_id}
            className="bg-[var(--aif-bg-surface)] rounded-xl border border-[var(--aif-border-default)] px-3 py-2.5 flex items-center justify-between"
          >
            <div>
              <div className="text-sm font-medium text-[var(--aif-text-primary)]">{r.program_name}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                  r.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                    : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400'
                }`}>
                  {r.status === 'approved' ? `✓ ${t('inventory.subsidyApproved')}` : `⌛ ${t('inventory.subsidyApplied')}`}
                </span>
                {r.days_until_payment != null && (
                  <span className="text-[10px] text-[var(--aif-text-muted)]">
                    {t('inventory.paymentIn', { days: r.days_until_payment })}
                  </span>
                )}
              </div>
            </div>
            <div className="text-base font-bold text-[var(--aif-text-primary)]">
              {formatEur(r.amount_eur)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Fixed Costs section ─────────────────────────────────────────────────────

function FixedCostsSection({ breakdown, totalAnnual }) {
  const { t } = useTranslation();
  if (!totalAnnual) return null;

  const typeLabel = { LAND_LEASE: t('contracts.LAND_LEASE'), INSURANCE: t('contracts.INSURANCE'), LOAN: t('contracts.LOAN') };

  return (
    <div data-testid="fixed-costs-section" className="bg-[var(--aif-bg-elevated)] rounded-xl px-4 py-3">
      <div className="flex justify-between items-center">
        <span className="text-xs text-[var(--aif-text-muted)]">{t('inventory.fixedCosts')}</span>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
          {formatEur(totalAnnual)} / {t('common.year', 'Jahr')}
        </span>
      </div>
      {breakdown?.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {breakdown.map((c, i) => (
            <div key={i} className="flex justify-between text-[10px] text-[var(--aif-text-muted)]">
              <span>{typeLabel[c.contract_type] || c.contract_type}
                {c.counterparty && ` · ${c.counterparty}`}
              </span>
              <span>{formatEur(c.annual_eur)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main InventoryDashboard component ──────────────────────────────────────

export default function InventoryDashboard({ token, onOpenFieldDetail }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch('/api/v1/aif/farm/inventory/valuation', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 px-4">
        <div className="text-2xl mb-2">⚠️</div>
        <p className="text-sm text-[var(--aif-text-muted)]">{t('map.loadError')}</p>
      </div>
    );
  }

  if (!data) return null;

  const { field_inventory, stored_inventory, subsidy_receivables, cost_breakdown, summary } = data;

  return (
    <div className="px-4 py-4 space-y-5 overflow-y-auto">
      <HeroSection summary={summary} />

      <FieldInventorySection
        fields={field_inventory}
        onFieldTap={onOpenFieldDetail}
      />

      {stored_inventory?.length > 0 && (
        <StoredInventorySection items={stored_inventory} />
      )}

      {subsidy_receivables?.length > 0 && (
        <SubsidySection receivables={subsidy_receivables} />
      )}

      <FixedCostsSection
        breakdown={cost_breakdown}
        totalAnnual={summary?.known_fixed_costs_annual}
      />
    </div>
  );
}
